import dayjs from 'dayjs';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { toast } from 'src/components/snackbar';
import Storage from 'src/utils/Storage';

import { allLangs } from './all-langs';
import { fallbackLng, changeLangMessages as messages } from './config-locales';

import type { LanguageValue } from './config-locales';

// ----------------------------------------------------------------------

export function useTranslate(ns?: string) {
  const { t, i18n } = useTranslation(ns);

  // Get fallback language details
  const fallback = allLangs.find((lang) => lang.value === fallbackLng) || allLangs[0];

  // Get the current language from i18n or fallback
  const currentLang = allLangs.find((lang) => lang.value === i18n.resolvedLanguage) || fallback;

  // Set the initial language based on Storage or fallback
  useEffect(() => {
    const storedLang = Storage.getItem('i18nextLng') as LanguageValue;

    if (storedLang && allLangs.some((lang) => lang.value === storedLang)) {
      i18n.changeLanguage(storedLang); // Set language in i18n
      dayjs.locale(allLangs.find((lang) => lang.value === storedLang)?.adapterLocale || 'en'); // Update Day.js locale
    } else {
      i18n.changeLanguage(fallback.value); // Use fallback language
      dayjs.locale(fallback.adapterLocale); // Update Day.js locale
    }
  }, [i18n, fallback]);

  // Function to handle language change
  const onChangeLang = useCallback(
    async (newLang: LanguageValue) => {
      try {
        const langChangePromise = i18n.changeLanguage(newLang); // Change language in i18n

        // Get messages for the new language
        const currentMessages = messages[newLang] || messages.en;

        // Show toast notifications
        toast.promise(langChangePromise, {
          loading: currentMessages.loading,
          success: () => currentMessages.success,
          error: currentMessages.error,
        });

        // Update Day.js locale
        dayjs.locale(allLangs.find((lang) => lang.value === newLang)?.adapterLocale || 'en');

        // Save selected language to storage
        Storage.removeItem('i18nextLng');
        Storage.setItem('i18nextLng', newLang);
      } catch (error) {
        console.error('Error changing language:', error);
      }
    },
    [i18n]
  );

  return {
    t,
    i18n,
    onChangeLang,
    currentLang,
  };
}
